import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useCustomers, useNextSQNumber, useCreateSalesQuotation, useUpdateSalesQuotation } from '@/hooks/useSalesOrder';
import type { CreateSQRequest, Customer, SQResponse } from '@/types/salesorder';
import {
  DEFAULT_CURRENCY,
  DEFAULT_VALID_DAYS,
  TRADE_TERMS_OPTIONS,
  CURRENCY_OPTIONS,
} from '@/types/salesorder';
import { calculateSQTotals, formatCurrency, formatDateRange } from '@/services/salesService';
import PartSelectionDialog from './PartSelectionDialog';
import type { PartCodeRecord } from '@/services/bomService';

interface SalesQuotationDialogProps {
  editingSQ?: SQResponse | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface LineItemInput {
  product_name: string;
  specification: string;
  quantity: number;
  unit_price: number;
  currency: string;
  remarks: string;
}

const emptyLineItem: LineItemInput = {
  product_name: '',
  specification: '',
  quantity: 0,
  unit_price: 0,
  currency: DEFAULT_CURRENCY,
  remarks: '',
};

const SalesQuotationDialog: React.FC<SalesQuotationDialogProps> = ({ editingSQ, onClose, onSuccess }) => {
  const { data: customers, isLoading: customersLoading } = useCustomers();
  const { data: nextSQNumber } = useNextSQNumber();
  const createSQMutation = useCreateSalesQuotation();
  const updateSQMutation = useUpdateSalesQuotation();

  const isEditMode = !!editingSQ;

  // Form state
  const [customerCode, setCustomerCode] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [customerAddress, setCustomerAddress] = useState('');
  const [tradeTerms, setTradeTerms] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [notes, setNotes] = useState('');
  const [validDays, setValidDays] = useState(DEFAULT_VALID_DAYS);
  const [lineItems, setLineItems] = useState<LineItemInput[]>([{ ...emptyLineItem }]);

  // Part selection dialog state
  const [showPartSelector, setShowPartSelector] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);

  // Calculate validity period based on quotation date
  const validFrom = quotationDate;
  const validUntil = quotationDate
    ? new Date(new Date(quotationDate).getTime() + validDays * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    : '';

  // Pre-populate form when editing
  useEffect(() => {
    if (editingSQ) {
      setCustomerCode(editingSQ.customer_code);
      setQuotationDate(editingSQ.quotation_date.split('T')[0]);
      setCustomerAddress(editingSQ.customer_address);
      setTradeTerms(editingSQ.trade_terms || '');
      setCurrency(editingSQ.currency);
      setNotes(editingSQ.notes || '');
      setLineItems(
        editingSQ.items.map((item) => ({
          product_name: item.product_name,
          specification: item.specification,
          quantity: item.quantity,
          unit_price: item.unit_price,
          currency: item.currency,
          remarks: item.remarks || '',
        }))
      );
    }
  }, [editingSQ]);

  // Calculate totals (no tax for quotations)
  const { subtotal } = calculateSQTotals(
    lineItems.map((item) => ({ quantity: item.quantity, unit_price: item.unit_price }))
  );

  // Update customer info when customer code changes
  useEffect(() => {
    if (customerCode && customers) {
      const customer = customers.find((c) => c.customer_id === customerCode);
      setSelectedCustomer(customer || null);
      // Auto-fill customer address if not already set
      if (customer && !customerAddress) {
        setCustomerAddress(customer.company_address || '');
      }
      // Auto-fill currency from customer preference
      if (customer && customer.currency && !isEditMode) {
        setCurrency(customer.currency);
      }
    } else {
      setSelectedCustomer(null);
    }
  }, [customerCode, customers, customerAddress, isEditMode]);

  // Update line item currency when main currency changes
  useEffect(() => {
    setLineItems((items) =>
      items.map((item) => ({ ...item, currency }))
    );
  }, [currency]);

  const handleAddLineItem = () => {
    setLineItems([...lineItems, { ...emptyLineItem, currency }]);
  };

  const handleRemoveLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const handleLineItemChange = (index: number, field: keyof LineItemInput, value: string | number) => {
    setLineItems((items) =>
      items.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleOpenPartSelector = (index: number) => {
    setEditingLineIndex(index);
    setShowPartSelector(true);
  };

  const handlePartSelected = (part: PartCodeRecord) => {
    if (editingLineIndex !== null) {
      setLineItems((items) =>
        items.map((item, i) =>
          i === editingLineIndex
            ? {
                ...item,
                product_name: part.vendor_product || part.part_number || '',
                specification: part.vendor_spec || '',
              }
            : item
        )
      );
    }
    setShowPartSelector(false);
    setEditingLineIndex(null);
  };

  const handleSubmit = async () => {
    // Validation
    if (!customerCode) {
      toast.error('Please select a customer');
      return;
    }
    if (!quotationDate) {
      toast.error('Please enter quotation date');
      return;
    }
    if (!customerAddress) {
      toast.error('Please enter customer address');
      return;
    }

    const validItems = lineItems.filter(
      (item) => item.product_name && item.quantity > 0 && item.unit_price > 0
    );
    if (validItems.length === 0) {
      toast.error('Please add at least one valid line item');
      return;
    }

    const request: CreateSQRequest = {
      customer_code: customerCode,
      quotation_date: quotationDate,
      customer_address: customerAddress,
      trade_terms: tradeTerms,
      currency,
      notes,
      valid_days: validDays,
      items: validItems,
    };

    try {
      if (isEditMode && editingSQ) {
        // Update existing SQ
        await updateSQMutation.mutateAsync({ id: editingSQ.sq_id, request });
        toast.success(`Sales Quotation ${editingSQ.sq_number} updated`);
      } else {
        // Create new SQ
        const result = await createSQMutation.mutateAsync(request);
        toast.success(`Sales Quotation ${result.sq_number} created`);
        if (result.sheet_url) {
          toast.success(
            <span>
              Sheet generated!{' '}
              <a href={result.sheet_url} target="_blank" rel="noopener noreferrer" className="underline">
                Open
              </a>
            </span>,
            { duration: 5000 }
          );
        }
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(`${isEditMode ? 'Update' : 'Create'} failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {isEditMode ? 'Edit Sales Quotation' : 'New Sales Quotation'}
              </h2>
              <p className="text-green-100 text-sm">
                {isEditMode
                  ? `Quotation No: ${editingSQ?.sq_number}`
                  : nextSQNumber
                  ? `Quotation No: ${nextSQNumber}`
                  : 'Loading...'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(95vh-180px)]">
          {/* Customer Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b pb-2">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Code <span className="text-red-500">*</span>
                </label>
                <select
                  value={customerCode}
                  onChange={(e) => setCustomerCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                  disabled={customersLoading}
                >
                  <option value="">Select customer...</option>
                  {customers?.map((customer) => (
                    <option key={customer.customer_id} value={customer.customer_id}>
                      {customer.customer_id} - {customer.chinese_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Name
                </label>
                <input
                  type="text"
                  value={selectedCustomer?.chinese_name || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tax ID
                </label>
                <input
                  type="text"
                  value={selectedCustomer?.tax_id || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Contact
                </label>
                <input
                  type="text"
                  value={selectedCustomer?.contact1 || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <input
                  type="text"
                  value={selectedCustomer?.phone || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  E-mail
                </label>
                <input
                  type="text"
                  value={selectedCustomer?.email1 || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
            </div>
          </div>

          {/* Quotation Info Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b pb-2">
              Quotation Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quotation Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={quotationDate}
                  onChange={(e) => setQuotationDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Valid Days
                </label>
                <input
                  type="number"
                  value={validDays}
                  onChange={(e) => setValidDays(parseInt(e.target.value) || DEFAULT_VALID_DAYS)}
                  min="1"
                  max="365"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Validity Period
                </label>
                <input
                  type="text"
                  value={formatDateRange(validFrom, validUntil)}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 dark:bg-gray-200 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Trade Terms
                </label>
                <select
                  value={tradeTerms}
                  onChange={(e) => setTradeTerms(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                >
                  <option value="">Select...</option>
                  {TRADE_TERMS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={customerAddress}
                  onChange={(e) => setCustomerAddress(e.target.value)}
                  placeholder="Enter customer address"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Currency
                </label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Line Items Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4 border-b pb-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Line Items</h3>
              <button
                type="button"
                onClick={handleAddLineItem}
                className="px-3 py-1 text-sm text-green-600 hover:bg-green-50 rounded-lg flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Item
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                    <th className="px-2 py-2 text-left w-10">No.</th>
                    <th className="px-2 py-2 text-left">Product Name</th>
                    <th className="px-2 py-2 text-left">Specification</th>
                    <th className="px-2 py-2 text-right w-24">Qty</th>
                    <th className="px-2 py-2 text-right w-28">Unit Price</th>
                    <th className="px-2 py-2 text-right w-28">Amount</th>
                    <th className="px-2 py-2 text-left">Remarks</th>
                    <th className="px-2 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, index) => (
                    <tr key={index} className="border-b dark:border-gray-600 text-gray-900 dark:text-gray-200">
                      <td className="px-2 py-2 text-center">{index + 1}</td>
                      <td className="px-2 py-2">
                        <div className="relative">
                          <input
                            type="text"
                            value={item.product_name}
                            onChange={(e) => handleLineItemChange(index, 'product_name', e.target.value)}
                            onClick={() => handleOpenPartSelector(index)}
                            placeholder="Click to select part..."
                            className="w-full px-2 py-1 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900 cursor-pointer"
                            readOnly
                          />
                          <button
                            type="button"
                            onClick={() => handleOpenPartSelector(index)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-green-600"
                            title="Select part from list"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={item.specification}
                          onChange={(e) => handleLineItemChange(index, 'specification', e.target.value)}
                          placeholder="Specification"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.quantity || ''}
                          onChange={(e) => handleLineItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          min="0"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.unit_price || ''}
                          onChange={(e) =>
                            handleLineItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)
                          }
                          min="0"
                          step="0.01"
                          className="w-full px-2 py-1 border border-gray-300 rounded text-right focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(item.quantity * item.unit_price, currency)}
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={item.remarks}
                          onChange={(e) => handleLineItemChange(index, 'remarks', e.target.value)}
                          placeholder="Remarks"
                          className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
                        />
                      </td>
                      <td className="px-2 py-2">
                        {lineItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals Section (no tax for quotations) */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b pb-2">
              Total
            </h3>
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-lg font-bold text-gray-900 dark:text-white">
                  <span>Subtotal:</span>
                  <span className="text-green-600 dark:text-green-400">{formatCurrency(subtotal, currency)}</span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  * Tax will be calculated when converting to Sales Order
                </p>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Additional notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-100 dark:border-gray-300 dark:text-gray-900"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={createSQMutation.isLoading || updateSQMutation.isLoading}
            className="px-4 py-2 text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {(createSQMutation.isLoading || updateSQMutation.isLoading) && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isEditMode ? 'Update Quotation' : 'Create Quotation'}
          </button>
        </div>
      </div>

      {/* Part Selection Dialog */}
      {showPartSelector && (
        <PartSelectionDialog
          onClose={() => {
            setShowPartSelector(false);
            setEditingLineIndex(null);
          }}
          onSelect={handlePartSelected}
        />
      )}
    </div>
  );
};

export default SalesQuotationDialog;
